// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Synthesis primitives (M3-21).
//
// **Samples, not nodes.** Every one-shot in this product is rendered by writing arithmetic into
// a `Float32Array` rather than by building an `OfflineAudioContext` graph, and that is a
// deliberate choice with three reasons behind it:
//
//   1. **Parity.** 07 §2 requires the Unity core to produce the same sounds as the web one, and
//      §9 requires a fixture suite that compares the rendered buffers. A loop over samples ports
//      to C# line for line. A graph of `BiquadFilterNode`s ports to a *different vendor's*
//      filter implementation and the tolerance has to absorb the difference.
//   2. **Testability.** A rendered buffer can be measured — peak, length, decay, where its
//      energy sits in the spectrum — so "the pop bumper is fatter and longer than the slingshot"
//      is an assertion rather than an opinion. None of that is reachable through a stubbed
//      `OfflineAudioContext`, because stubbing one means re-implementing Web Audio.
//   3. **Cost.** 07 §9 budgets ≤ 220 buffers in ≤ 40 ms on the low tier. A tight numeric loop
//      is the cheapest way to hit that, and it costs no graph construction at all.
//
// **No `Math.sin` in any inner loop.** Every oscillator here is a *rotating phasor*: two numbers
// holding the cosine and sine of the current phase, advanced by a 2×2 rotation of four multiplies
// and two adds. The identity is the addition formula and nothing more —
//
//     cos(φ+θ) = cos φ · cos θ − sin φ · sin θ
//     sin(φ+θ) = sin φ · cos θ + cos φ · sin θ
//
// — so `cos θ` and `sin θ` are computed **once per oscillator** instead of once per sample. A
// 520 ms chime bar is twenty-five thousand samples and four partials; that is a hundred thousand
// library calls replaced by six.
//
// It is faster, and that is the smaller reason. The larger one is **parity**: 07 §9 requires the
// Unity core to produce the same buffers as this one, and `Math.sin` is a platform transcendental
// whose last bits are not specified and do differ between runtimes. Four multiplies are four
// multiplies in every language. Replacing the library call removes the only place in the audio
// layer where the two cores could disagree for a reason neither of them controls.
//
// A rotation preserves length exactly in real arithmetic and very nearly in binary64 — the drift
// is about 1e-16 a sample — so a gliding oscillator renormalises at each block boundary, which
// costs one square root per 64 samples and removes the question entirely.
//
// Everything here is real-time-safe in the sense that matters: it runs at load, once, and never
// during playback.
//
// The filters are the RBJ cookbook biquads, which is the same maths every audio toolkit uses,
// so a reader who knows the reference recognises the coefficients rather than having to trust
// them.

/** A sample buffer being written into. Mono; the panner gives it a position later. */
export type Samples = Float32Array

/** Samples for a duration in milliseconds. At least one, so a zero-length buffer never exists. */
export function samplesFor(ms: number, sampleRate: number): number {
  return Math.max(1, Math.round((ms / 1000) * sampleRate))
}

/** ln(1000). The per-decay-time exponent that puts an envelope 60 dB down. */
const LN_1E3 = 6.907755278982137

/**
 * Where an envelope stops being worth computing. −120 dBFS, which is below every device.
 */
const ENVELOPE_FLOOR = 1e-6

/**
 * Exponential decay to −60 dB over `ms`.
 *
 * Exponential rather than linear because that is what a struck object does, and the difference
 * is not subtle: a linear decay has an audible corner where it reaches zero, which reads as the
 * sound being cut off rather than dying away.
 */
export function decayCoefficient(ms: number, sampleRate: number): number {
  return Math.exp(-LN_1E3 / Math.max(1, (ms / 1000) * sampleRate))
}

/**
 * How many samples an exponential envelope is worth writing, before it is under the floor.
 *
 * **This replaced `if (envelope < 1e-6) break`, and the reason is a real defect that the Unity
 * port found.** Both loops did the same arithmetic, so both were expected to stop on the same
 * sample. They did not: `impactGlass` stopped one sample earlier in V8 than in .NET, and because
 * a cue's eight variants are drawn from **one** noise stream, that single extra draw shifted
 * every variant after the first onto different noise. Seven of the eight buffers were different
 * sounds.
 *
 * The cause is a knife edge. The envelope is `amplitude · decay^i` accumulated by repeated
 * multiplication, and the floor is crossed when `i > S · ln(amplitude · 10⁶) / ln(1000)`, where
 * `S` is the decay time in samples. When the amplitude is exactly 1 that is exactly `2S` — an
 * integer whenever the decay is a whole number of milliseconds at 48 kHz, which describes most of
 * the table. Landing exactly *on* the threshold, the comparison is decided by whichever way
 * several hundred accumulated roundings happened to fall, and `Math.exp` is a platform
 * transcendental whose last bit is not specified. `impactMetal` and `impactGlass` are the same
 * shape and the same knife edge; metal happened to agree and glass happened not to.
 *
 * So the count is computed **once, in closed form, from the index** rather than by watching the
 * envelope. For the amplitude-of-1 case — which is the case that ties — it is `2S + 1` with no
 * transcendental in it at all, so the two runtimes cannot disagree. For any other amplitude the
 * result would have to be exactly integral to tie, which a logarithm of a number like 0.35 is
 * not going to be.
 *
 * Returns `Infinity` for an envelope that never decays, which is how the buzzers and the ambience
 * beds ask to run for the whole buffer.
 */
export function decayCutoff(amplitude: number, decayMs: number, sampleRate: number): number {
  const a = Math.abs(amplitude)
  // Already under the floor. One sample, which is what the old loop wrote before breaking.
  if (!(a > ENVELOPE_FLOOR)) return 1
  const s = Math.max(1, (decayMs / 1000) * sampleRate)
  if (!Number.isFinite(s)) return Number.POSITIVE_INFINITY
  // `i > S · (2 + ln(a)/ln(1000))`, and 2 is exact because ln(10⁶) is exactly twice ln(10³).
  const exact = s * (2 + Math.log(a) / LN_1E3)
  return Number.isFinite(exact) ? Math.floor(exact) + 1 : Number.POSITIVE_INFINITY
}

/**
 * A source of noise with its own stream.
 *
 * 07 rule 4: audio has its own generator, because the simulation's is reserved for physics. A
 * replay's *sound* may vary while its ball may not, and sharing the stream would make every
 * baked variant a change to the game.
 */
export interface NoiseSource {
  /** Uniform in [−1, 1). */
  next: () => number
}

/** A one-pole lowpass. Gentle, cheap, and the right shape for "muffled". */
export class OnePole {
  private z = 0
  private readonly a: number

  constructor(cutoffHz: number, sampleRate: number) {
    // The standard one-pole coefficient. Clamped, because a cutoff at or above Nyquist is a
    // filter that does nothing and a negative one is a filter that explodes.
    const x = Math.exp((-2 * Math.PI * Math.min(cutoffHz, sampleRate * 0.49)) / sampleRate)
    this.a = Math.min(Math.max(x, 0), 0.9999)
  }

  lowpass(input: number): number {
    this.z = input * (1 - this.a) + this.z * this.a
    return this.z
  }

  highpass(input: number): number {
    return input - this.lowpass(input)
  }
}

/** A direct-form-I biquad. Coefficients from the RBJ cookbook. */
export class Biquad {
  private x1 = 0
  private x2 = 0
  private y1 = 0
  private y2 = 0
  private b0 = 1
  private b1 = 0
  private b2 = 0
  private a1 = 0
  private a2 = 0

  static bandpass(centreHz: number, q: number, sampleRate: number): Biquad {
    return new Biquad().setBandpass(centreHz, q, sampleRate)
  }

  static highpass(cutoffHz: number, q: number, sampleRate: number): Biquad {
    return new Biquad().setHighpass(cutoffHz, q, sampleRate)
  }

  static lowpass(cutoffHz: number, q: number, sampleRate: number): Biquad {
    return new Biquad().setLowpass(cutoffHz, q, sampleRate)
  }

  /**
   * Re-tune in place, keeping the filter's memory.
   *
   * The statics above are the ordinary way to make a filter; this is what a *swept* one needs.
   * Keeping `x1…y2` across the change is the whole point: a sweep that allocated a new filter
   * every few samples would restart from silence each time and turn a smooth glide into a
   * sequence of tiny clicks.
   */
  setBandpass(centreHz: number, q: number, sampleRate: number): this {
    const w = (2 * Math.PI * Math.min(centreHz, sampleRate * 0.49)) / sampleRate
    const alpha = Math.sin(w) / (2 * Math.max(q, 0.05))
    const a0 = 1 + alpha
    // Constant *peak* gain form: the passband tops out at unity whatever Q is, so raising Q
    // narrows the band without also making it louder. The other form makes every high-Q filter
    // a volume change as well as a timbre change.
    this.b0 = alpha / a0
    this.b1 = 0
    this.b2 = -alpha / a0
    this.a1 = (-2 * Math.cos(w)) / a0
    this.a2 = (1 - alpha) / a0
    return this
  }

  setHighpass(cutoffHz: number, q: number, sampleRate: number): this {
    const w = (2 * Math.PI * Math.min(cutoffHz, sampleRate * 0.49)) / sampleRate
    const alpha = Math.sin(w) / (2 * Math.max(q, 0.05))
    const cos = Math.cos(w)
    const a0 = 1 + alpha
    this.b0 = (1 + cos) / 2 / a0
    this.b1 = (-(1 + cos)) / a0
    this.b2 = (1 + cos) / 2 / a0
    this.a1 = (-2 * cos) / a0
    this.a2 = (1 - alpha) / a0
    return this
  }

  setLowpass(cutoffHz: number, q: number, sampleRate: number): this {
    const w = (2 * Math.PI * Math.min(cutoffHz, sampleRate * 0.49)) / sampleRate
    const alpha = Math.sin(w) / (2 * Math.max(q, 0.05))
    const cos = Math.cos(w)
    const a0 = 1 + alpha
    this.b0 = (1 - cos) / 2 / a0
    this.b1 = (1 - cos) / a0
    this.b2 = (1 - cos) / 2 / a0
    this.a1 = (-2 * cos) / a0
    this.a2 = (1 - alpha) / a0
    return this
  }

  process(x: number): number {
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2
    this.x2 = this.x1
    this.x1 = x
    this.y2 = this.y1
    this.y1 = y
    return y
  }
}

/**
 * Add a decaying sine to a buffer.
 *
 * The building block of every modal sound in the product — a chime bar, a bumper's ring, a
 * metal impact's ping — because a struck rigid body *is* a sum of decaying sinusoids, and
 * modelling it that way is both correct and about six lines.
 */
export function addPartial(
  out: Samples,
  sampleRate: number,
  frequencyHz: number,
  amplitude: number,
  decayMs: number,
  phase = 0,
): void {
  // **A partial above Nyquist is not a partial, it is an alias**, and it is worth refusing rather
  // than writing. Found on a Quest 3, where the audio device came up at 24 kHz rather than 48: the
  // tilt bob's top partial is 12,960 Hz, which at that rate folds back to 11,040 and turns a thin
  // metallic ring into a mistuned one. The rate is forced to 48 kHz now (M7-11), where nothing in
  // the machine comes close to the limit — so this is a guard that costs nothing on the hardware
  // this product ships to and keeps it honest on hardware that refuses.
  if (!(Math.abs(frequencyHz) < sampleRate * 0.5)) return
  const step = (2 * Math.PI * frequencyHz) / sampleRate
  const cosStep = Math.cos(step)
  const sinStep = Math.sin(step)
  const decay = decayCoefficient(decayMs, sampleRate)

  // The phasor: `s` is the sample, `c` is its quadrature partner and exists only to advance it.
  // Fixed frequency, so no renormalising — the buffer runs out long before 1e-16 a sample
  // amounts to anything, and the envelope is falling the whole time regardless.
  let c = Math.cos(phase)
  let s = Math.sin(phase)
  let envelope = amplitude
  const end = Math.min(out.length, decayCutoff(amplitude, decayMs, sampleRate))

  for (let i = 0; i < end; i++) {
    out[i]! += s * envelope
    const next = c * cosStep - s * sinStep
    s = s * cosStep + c * sinStep
    c = next
    envelope *= decay
  }
}

/**
 * Add a sine whose pitch falls exponentially from one frequency to another.
 *
 * The body of every solenoid (07 §3.1): the coil frame rings, and the ring drops in pitch as
 * the plunger seats. A fixed-pitch body sounds like a beep; the drop is what makes it a thump.
 */
export function addPitchDrop(
  out: Samples,
  sampleRate: number,
  fromHz: number,
  toHz: number,
  glideMs: number,
  amplitude: number,
  decayMs: number,
): void {
  const decay = decayCoefficient(decayMs, sampleRate)
  const glideSamples = Math.max(1, (glideMs / 1000) * sampleRate)
  // Per-sample ratio that takes `fromHz` to `toHz` across the glide. Exponential, so the pitch
  // falls by a constant *interval* per unit time, which is what the ear hears as smooth.
  const ratio = Math.pow(Math.max(toHz, 1) / Math.max(fromHz, 1), 1 / glideSamples)
  // The same fall, taken in one step per block. A 60 ms glide is still forty-five of them.
  const perBlock = Math.pow(ratio, GLIDE_BLOCK)

  let frequency = fromHz
  let cosStep = 1
  let sinStep = 0
  let c = 1
  let s = 0
  let envelope = amplitude
  const end = Math.min(out.length, decayCutoff(amplitude, decayMs, sampleRate))

  for (let i = 0; i < end; i++) {
    if (i % GLIDE_BLOCK === 0) {
      if (i > 0) {
        if (frequency > toHz) frequency = Math.max(toHz, frequency * perBlock)
        // A rotation is norm-preserving in real arithmetic and very slightly not in binary64.
        // One square root a block puts the phasor back on the unit circle, which matters here
        // and not in `addPartial` because a glide runs for the whole buffer rather than dying
        // away under its own envelope.
        const magnitude = Math.sqrt(c * c + s * s)
        if (magnitude > 0) {
          c /= magnitude
          s /= magnitude
        }
      }
      const step = (2 * Math.PI * frequency) / sampleRate
      cosStep = Math.cos(step)
      sinStep = Math.sin(step)
    }

    out[i]! += s * envelope
    const next = c * cosStep - s * sinStep
    s = s * cosStep + c * sinStep
    c = next
    envelope *= decay
  }
}

/** Add a filtered noise burst with an exponential decay. Transients and mechanism noise. */
export function addNoiseBurst(
  out: Samples,
  sampleRate: number,
  noise: NoiseSource,
  filter: Biquad | undefined,
  amplitude: number,
  decayMs: number,
): void {
  const decay = decayCoefficient(decayMs, sampleRate)
  let envelope = amplitude
  // The cutoff decides how many values are taken from the stream, so it is the one place in the
  // layer where a one-sample disagreement between two runtimes is not inaudible — see
  // `decayCutoff`.
  const end = Math.min(out.length, decayCutoff(amplitude, decayMs, sampleRate))
  for (let i = 0; i < end; i++) {
    const raw = noise.next()
    out[i]! += (filter === undefined ? raw : filter.process(raw)) * envelope
    envelope *= decay
  }
}

/**
 * How often anything that glides recomputes its coefficients, in samples.
 *
 * A swept filter and a falling pitch have the same problem: the thing being changed is defined
 * by a `sin` and a `cos`, and recomputing them every sample is the expensive part of the whole
 * synthesis layer. Sixty-four samples is 1.3 ms at 48 kHz — a 60 ms coil glide is still
 * forty-five steps, and the ear resolves neither a filter's centre nor a glide's rate at that
 * resolution.
 *
 * Fixed rather than derived, because the C# core has to pick the same number or the two renders
 * diverge in a way 07 §9's fixture suite would have to widen its tolerance to absorb.
 */
export const GLIDE_BLOCK = 64

/**
 * Filtered noise whose band slides from one frequency to another.
 *
 * A ball rolling down into the trough: the sound is not a pitch falling, it is a *resonance*
 * falling — the same broadband rattle heard through a smaller and smaller space as the ball
 * drops away under the playfield. A fixed filter cannot do it and a pitch drop is the wrong
 * shape, because there is no tone there to drop.
 *
 * Exponential in frequency, like every other glide here, so the fall reads as smooth rather
 * than as fast-then-slow.
 */
export function addSweptNoise(
  out: Samples,
  sampleRate: number,
  noise: NoiseSource,
  fromHz: number,
  toHz: number,
  q: number,
  amplitude: number,
  decayMs: number,
  sweepMs: number,
): void {
  const decay = decayCoefficient(decayMs, sampleRate)
  const sweepSamples = Math.max(1, (sweepMs / 1000) * sampleRate)
  const ratio = Math.pow(Math.max(toHz, 1) / Math.max(fromHz, 1), 1 / sweepSamples)
  const filter = new Biquad()
  let centre = fromHz
  let envelope = amplitude
  const end = Math.min(out.length, decayCutoff(amplitude, decayMs, sampleRate))

  for (let i = 0; i < end; i++) {
    if (i % GLIDE_BLOCK === 0) filter.setBandpass(centre, q, sampleRate)
    out[i]! += filter.process(noise.next()) * envelope
    centre = toHz < fromHz ? Math.max(toHz, centre * ratio) : Math.min(toHz, centre * ratio)
    envelope *= decay
  }
}

/**
 * A feedback comb, in place. The delay line a spring is.
 *
 * A helical spring is a delay: a disturbance runs to the far end, reflects, and comes back. That
 * is what a comb filter models, and it is why a plucked spring goes *boing* at a pitch set by its
 * length rather than by anything you did to it. 07 §3.6 asks for the plunger release as "a spring
 * twang (a comb-filtered impulse)", which is that observation stated as a recipe.
 *
 * `delaySamples` sets the pitch — `sampleRate / delaySamples` Hz — and `feedback` sets how long
 * it rings. The damping term is what makes it a spring rather than a tube: each pass round the
 * loop loses its top end, so the twang darkens as it dies, exactly as `addPartial`'s higher
 * partials do.
 */
export function combInPlace(
  out: Samples,
  delaySamples: number,
  feedback: number,
  damping = 0.35,
): void {
  const delay = Math.max(1, Math.round(delaySamples))
  const gain = Math.min(Math.max(feedback, 0), 0.999)
  let previous = 0
  for (let i = delay; i < out.length; i++) {
    // One-pole lowpass inside the loop: the reflection comes back duller than it left.
    previous = out[i - delay]! * (1 - damping) + previous * damping
    out[i] = out[i]! + previous * gain
  }
}

/**
 * Make a rendered buffer loop without a seam, by folding its tail back over its head.
 *
 * `raw` holds `length + seam` samples: the loop itself, plus the signal the generator produced
 * *next*. Cross-fading that tail over the head means the sample after the last one is the one
 * that genuinely followed it, so the join has no step in it. A loop cut with a hard edge ticks
 * once per lap — quiet enough to survive review, and once heard, the only thing you can hear.
 *
 * The caller owns the phase question: anything periodic baked into `raw` must complete a whole
 * number of cycles in `length`, because the fold hides a discontinuity in the waveform and
 * cannot hide one in the envelope.
 */
export function loopSeamless(raw: Samples, length: number, seam: number): Samples {
  const out = new Float32Array(length)
  out.set(raw.subarray(0, length))
  const n = Math.min(seam, length)
  for (let i = 0; i < n; i++) {
    const t = i / n
    out[i] = raw[i]! * t + (raw[length + i] ?? 0) * (1 - t)
  }
  return out
}

/**
 * Add a square wave with a decaying envelope.
 *
 * A buzzer is not a sine. 07 §3.6 asks for the tilt warning as "a two-tone buzzer, 220 / 180 Hz
 * square with a hard gate", and the odd harmonics of a square are the entire reason a buzzer is
 * unpleasant enough to be a warning — a sine at the same pitch is a doorbell.
 *
 * Built from the same rotating phasor as everything else and taken by its sign, so it costs one
 * comparison more than a sine and no library call at all.
 */
export function addSquare(
  out: Samples,
  sampleRate: number,
  frequencyHz: number,
  amplitude: number,
  decayMs: number,
  from = 0,
  to = out.length,
): void {
  const step = (2 * Math.PI * frequencyHz) / sampleRate
  const cosStep = Math.cos(step)
  const sinStep = Math.sin(step)
  const decay = decayCoefficient(decayMs, sampleRate)
  let c = 1
  let s = 0
  let envelope = amplitude
  const start = Math.max(0, from)
  const end = Math.min(Math.min(to, out.length), start + decayCutoff(amplitude, decayMs, sampleRate))
  for (let i = start; i < end; i++) {
    out[i]! += (s >= 0 ? envelope : -envelope)
    const next = c * cosStep - s * sinStep
    s = s * cosStep + c * sinStep
    c = next
    envelope *= decay
  }
}

/** Run a whole buffer through a filter, in place. For a muffling pass over the finished sound. */
export function filterInPlace(out: Samples, filter: Biquad | OnePole, lowpass = true): void {
  for (let i = 0; i < out.length; i++) {
    const x = out[i]!
    out[i] = filter instanceof Biquad ? filter.process(x) : lowpass ? filter.lowpass(x) : filter.highpass(x)
  }
}

/**
 * Fade the last few milliseconds to zero.
 *
 * A buffer that still has signal in its final sample clicks when it stops, every time it plays.
 * Two milliseconds is inaudible as a fade and completely removes it.
 */
export function fadeOut(out: Samples, sampleRate: number, ms = 2): void {
  const n = Math.min(out.length, samplesFor(ms, sampleRate))
  for (let i = 0; i < n; i++) {
    // `(i + 1) / n`, so the final sample is multiplied by zero rather than by `1/n`. With the
    // other form every buffer ended on a real value — inaudibly small, but the invariant
    // "a buffer ends at silence" was not actually true, and an invariant that is nearly true
    // is one no test can rest on.
    out[out.length - n + i]! *= 1 - (i + 1) / n
  }
}

/** Peak absolute sample. */
export function peakOf(buffer: Samples): number {
  let peak = 0
  for (let i = 0; i < buffer.length; i++) {
    const v = Math.abs(buffer[i]!)
    if (v > peak) peak = v
  }
  return peak
}

/**
 * Scale so the loudest sample sits at `target`.
 *
 * Every one-shot is normalised, and the *mix* decides how loud it actually is. Without this a
 * recipe change that happens to add 3 dB silently re-balances the whole machine, and the person
 * who made it was adjusting a filter.
 */
export function normalise(buffer: Samples, target = 0.9): void {
  const peak = peakOf(buffer)
  if (peak <= 0) return
  const scale = target / peak
  for (let i = 0; i < buffer.length; i++) buffer[i]! *= scale
}
